import React, { useState, useEffect } from 'react';
import { validerAarsak, validerKonsekvens } from '../utils/paragraf-2-22';
import './BygningsdelVurdering.css';

function BygningsdelVurdering({ bygningsdeler, onChange, vis222Hjelp, setVis222Hjelp, paragraf222Info }) {
  const [valideringer, setValideringer] = useState({});

  const kreverAarsakKonsekvens = (tg) => {
    return tg === 'TG2' || tg === 'TG3';
  };

  useEffect(() => {
    // Valider alle bygningsdeler når de endres
    const nyeValideringer = {};
    bygningsdeler.forEach(del => {
      if (kreverAarsakKonsekvens(del.tg)) {
        nyeValideringer[`${del.id}_aarsak`] = validerAarsak(del.aarsak, del.tg);
        nyeValideringer[`${del.id}_konsekvens`] = validerKonsekvens(del.konsekvens, del.tg);
      }
    });
    setValideringer(nyeValideringer);
  }, [bygningsdeler]);

  const getValideringsMelding = (delId, felt) => {
    const validering = valideringer[`${delId}_${felt}`];
    if (!validering) return null;

    return (
      <div className={`validering-melding validering-${validering.nivaa}`}>
        {validering.nivaa === 'feil' && '❌ '}
        {validering.nivaa === 'advarsel' && '⚠️ '}
        {validering.nivaa === 'tips' && '💡 '}
        {validering.nivaa === 'ok' && '✅ '}
        {validering.melding}
      </div>
    );
  };

  return (
    <div className="bygningsdeler-seksjon">
      <div className="bygningsdeler-header">
        <h3>Bygningsdeler - Tilstandsvurdering</h3>
        <button
          type="button"
          onClick={() => setVis222Hjelp(!vis222Hjelp)}
          className="btn-hjelp-222"
        >
          ℹ️ §2-22 Hjelp
        </button>
      </div>

      <p className="bygningsdeler-info">
        Vurder tilstand for hver bygningsdel. <strong>TG2 og TG3 krever årsak og konsekvens</strong> (Forskrift §2-22)
      </p>

      {vis222Hjelp && (
        <div className="paragraf-222-hjelp">
          <div className="hjelp-header">
            <h4>{paragraf222Info.tittel}</h4>
            <button onClick={() => setVis222Hjelp(false)} className="btn-lukk">✕</button>
          </div>
          <div className="hjelp-innhold">
            <p style={{whiteSpace: 'pre-line'}}>{paragraf222Info.forklaring}</p>

            <div className="hjelp-eksempel">
              <h5>TG2 - Eksempler:</h5>
              <p><strong>Årsak:</strong> {paragraf222Info.eksempler.TG2.eksempel_aarsak.join(', ')}</p>
              <p><strong>Konsekvens:</strong> {paragraf222Info.eksempler.TG2.eksempel_konsekvens.join(', ')}</p>
            </div>

            <div className="hjelp-eksempel tg3">
              <h5>TG3 - Eksempler:</h5>
              <p><strong>Årsak:</strong> {paragraf222Info.eksempler.TG3.eksempel_aarsak.join(', ')}</p>
              <p><strong>Konsekvens:</strong> {paragraf222Info.eksempler.TG3.eksempel_konsekvens.join(', ')}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bygningsdeler-liste">
        {bygningsdeler.map((del) => (
          <div key={del.id} className="bygningsdel-card">
            <h4>{del.navn}</h4>

            <div className="form-group">
              <label>Beskrivelse (materiale, utseende)</label>
              <input
                type="text"
                value={del.beskrivelse}
                onChange={(e) => onChange(del.id, 'beskrivelse', e.target.value)}
                placeholder="F.eks. Vinylbelegg, keramiske fliser, malt panel..."
              />
            </div>

            <div className="form-group">
              <label>Tilstandsgrad</label>
              <select
                value={del.tg}
                onChange={(e) => onChange(del.id, 'tg', e.target.value)}
              >
                <option value="">Ikke vurdert</option>
                <option value="TG0">TG0 - Som nytt</option>
                <option value="TG1">TG1 - Meget god</option>
                <option value="TG2">TG2 - God (vedlikeholdsetterslep)</option>
                <option value="TG3">TG3 - Dårlig (tiltak nødvendig)</option>
                <option value="TGIU">TGIU - Ikke undersøkt</option>
              </select>
            </div>

            {del.tg === 'TGIU' && (
              <div className="tgiu-seksjon">
                <div className="tgiu-advarsel">
                  ⚠️ TGIU krever begrunnelse: Hvorfor er bygningsdelen ikke undersøkt?
                </div>

                <div className="form-group">
                  <label>
                    Begrunnelse for TGIU * <span className="hjelp-tekst">(Hvorfor ikke undersøkt?)</span>
                  </label>
                  <textarea
                    value={del.tgiu_begrunnelse || ''}
                    onChange={(e) => onChange(del.id, 'tgiu_begrunnelse', e.target.value)}
                    placeholder="F.eks. 'Innredning/møbler hindrer tilgang', 'Bygningsdel er innkapslet/ikke tilgjengelig', 'Krever destruktiv undersøkelse'"
                    rows="3"
                    required
                  />
                  <div className="tgiu-hjelp">
                    💡 Vanlige begrunnelser:
                    <ul>
                      <li>Bygningsdelen er ikke tilgjengelig (innredning, møbler, teppe)</li>
                      <li>Krever destruktiv undersøkelse som ikke er avtalt</li>
                      <li>Sikkerhetsmessige årsaker</li>
                      <li>Tekniske begrensninger</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {kreverAarsakKonsekvens(del.tg) && (
              <div className="aarsak-konsekvens-seksjon">
                <div className="paragraf-advarsel">
                  ⚠️ Forskrift §2-22: Du må beskrive årsak og konsekvens for {del.tg}
                </div>

                <div className="form-group">
                  <label>
                    Årsak * <span className="hjelp-tekst">(Hva forårsaket tilstanden?)</span>
                  </label>
                  <textarea
                    value={del.aarsak}
                    onChange={(e) => onChange(del.id, 'aarsak', e.target.value)}
                    placeholder={paragraf222Info.mal_tekst[del.tg]?.aarsak_mal || 'Beskriv årsak...'}
                    rows="3"
                    required
                  />
                  {getValideringsMelding(del.id, 'aarsak')}
                </div>

                <div className="form-group">
                  <label>
                    Konsekvens * <span className="hjelp-tekst">(Hva er følgene?)</span>
                  </label>
                  <textarea
                    value={del.konsekvens}
                    onChange={(e) => onChange(del.id, 'konsekvens', e.target.value)}
                    placeholder={paragraf222Info.mal_tekst[del.tg]?.konsekvens_mal || 'Beskriv konsekvens...'}
                    rows="3"
                    required
                  />
                  {getValideringsMelding(del.id, 'konsekvens')}
                </div>

                {del.tg === 'TG3' && (
                  <div className="form-group">
                    <label>
                      Kostnadsestimat * <span className="hjelp-tekst">(Estimert utbedringskostnad)</span>
                    </label>
                    <select
                      value={del.kostnadsestimat}
                      onChange={(e) => onChange(del.id, 'kostnadsestimat', e.target.value)}
                      required
                      className="kostnadsestimat-select"
                    >
                      <option value="">Velg prisintervall...</option>
                      <option value="under_25000">Under 25.000 kr</option>
                      <option value="25000_50000">25.000 - 50.000 kr</option>
                      <option value="50000_100000">50.000 - 100.000 kr</option>
                      <option value="100000_200000">100.000 - 200.000 kr</option>
                      <option value="200000_300000">200.000 - 300.000 kr</option>
                      <option value="over_300000">Over 300.000 kr</option>
                      <option value="ikke_estimert">Kan ikke estimeres (krever nærmere undersøkelse)</option>
                    </select>
                    <div className="kostnadsinfo">
                      💡 TG3 krever kostnadsestimat. Velg intervall basert på normal utbedringskostnad for denne typen skade.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default BygningsdelVurdering;
